import React, { useState } from 'react'
import Navbar from '../../components/Navbar/Navbar'
import { Button, Form, Input, Modal, Table, Row } from 'antd'
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { usePostRequest } from '../../hooks/usePostRequest'
import { UseGetRequest } from '../../utils/api/ApiRequests'
import { useTheme, useMediaQuery } from '@mui/material'
import { removeUnderscoreToSentence } from '../../utils/Common'

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false);
  
  // Watch all values
  const values = Form.useWatch([], form);
    React.useEffect(() => {
      form.validateFields({
        validateOnly: true,
      }).then(() => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      },
      );
    }, [values]);
    return (
    <Button type="primary" htmlType="submit" disabled={!submittable} loading={isLoading}>Submit</Button>
    );
};

const EditSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false);
  
  // Watch all values
  const values = Form.useWatch([], form);
    React.useEffect(() => {
      form.validateFields({
        validateOnly: true,
      }).then(() => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      },
      );
    }, [values]);
    
    return (
    <Button type="primary" htmlType="submit" disabled={!submittable} loading={isLoading}>Save Changes</Button>
    );
};


export default function BusinessUnit () {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // Get the screen size
  const theme = useTheme()
  const isPhoneScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {callPost, isLoading} = usePostRequest(()=>{
    setShowAddModal(false); 
    setShowEditModal(false);
    setShowViewModal(false);
    resetFields();
    setFilterConfig((prev) => ({...prev, is_updated: !prev.is_updated}))});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [clickedRowData, setClickedRowData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [verticalTableData, setVerticalTableData] = useState([]);
  const [modal, contextHolder] = Modal.useModal();
  const [filterConfig, setFilterConfig] = useState({
    is_updated: false
  });
  const [isFetching, setIsFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);
  
  const tableColumns = [
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
    },
  ];

  const keyValuePairColumns = [
    {
      title: '',
      dataIndex: 'header',
      key: 'header',
      render: (data) => (
        <div className='bold'>{removeUnderscoreToSentence(data)}</div>
      )
    },
    {
      title: '',
      dataIndex: 'value',
      key: 'value',
    }
  ]

  const confirmDelete = (businessUnit) => {
    modal.confirm({
      title: `Are you sure you want to delete business unit "${businessUnit.name}"?`,
      icon: <ExclamationCircleOutlined />,
      content: 'You cannot undo this action.',
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: ()=>handleDelete(businessUnit.id),
      okType: 'danger',
    });
  };

  const onFinish = (formInputs) => {
    callPost(
      "business_units/add", 
      formInputs,
      false
    );
  };

  const onEdit = (formInputs) => {
    callPost(
      `business_units/update/${clickedRowData.id}`, 
      formInputs,
      false
    );
  };

  const handleSearch = (e) => {
    if (e.key==="Enter") {
      setFilterConfig((prev) => ({...prev, term: searchTerm}));
    }
  }

  function handleDelete(passedId) {
    callPost("business_units/delete/" + passedId,  {}, false);
  }

  const handleRowClick = (rowData) => {
    setClickedRowData(rowData);
    editForm.setFieldsValue(rowData); 
    setShowViewModal(true);
  };

  const rowProps = (record) => ({
    onClick: () => handleRowClick(record),
  });

  function resetFields () {
    form.resetFields();
    editForm.resetFields();
  }

  React.useEffect(() => {
    const searchTimeout = setTimeout(() => {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }));
    }, 2000);

    return () => {
      clearTimeout(searchTimeout);
    };
  }, [searchTerm]);

React.useEffect(() => {
  async function fetchTableData  () {
    setIsFetching(true);
    setTableData([]);
    setVerticalTableData([]);
    const response = await UseGetRequest("business_units/get", filterConfig);
    if (response.data) {
        setTableData(response.data.data);

        // CREATE KEY-VALUE-PAIR TABLE DATA FOR VERTICAL TABLE
        // create a new array of object containing header and value
        const tempTableData = response.data.data.map(row => (
          Object.entries(row).map(([header, value]) => ({ header, value, ...row}))
        ));
        // extract only the objects we need for the table
        const filteredDataSource = tempTableData.flat().filter(record => tableColumns.some(column => column.dataIndex === record.header));
        setVerticalTableData(filteredDataSource);
    }
    setIsFetching(false);
  }

    fetchTableData ();
}, [filterConfig]);


  return (
    <Navbar pageTitle="Business Units" pageButtonTitle="Add Business Unit"  setSearchQuery={(e)=>setSearchTerm(e)} onSearch={handleSearch} searchPlaceholder="business unit" onClickButton={()=>setShowAddModal(true)} isManager searchable>
        <Table className='mt-4' rowKey="id"
          onRow={(record) => ({
            ...rowProps(record), style: { cursor: 'pointer' },
          })} 
          columns={isPhoneScreen?keyValuePairColumns:tableColumns} 
          dataSource={isPhoneScreen?verticalTableData:tableData}
          loading={isFetching || isLoading} />

        <Modal
          title="Add Business Unit"
          centered
          open={showAddModal}
          footer={null}
          onCancel={() => {
            setShowAddModal(false); 
            form.resetFields();
          }}
        >
          <Form className='mt-3 mx-3' form={form} name="validateOnly" layout='vertical' onFinish={onFinish}>
            <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
              <Input />
            </Form.Item>

            <Form.Item>
              <Row className='d-flex justify-content-end'>
                <Row>
                  <Button className='me-2' danger onClick={()=>setShowAddModal(false)} disabled={isLoading}>Cancel</Button>
                  <AddSubmitButton form={form} isLoading={isLoading} />
                </Row>
              </Row>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Edit Business Unit"
          centered
          open={showEditModal}
          footer={null}
          onCancel={() => {
            setShowEditModal(false); 
            editForm.resetFields();
          }}
        >
          <Form className='mt-3 mx-3' form={editForm} name="editForm" layout='vertical' onFinish={onEdit}>
            <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
              <Input/>
            </Form.Item>

            <Form.Item>
              <Row className='d-flex justify-content-end'>
                  <Button danger onClick={()=>setShowEditModal(false)} disabled={isLoading}>Cancel</Button>
                  <EditSubmitButton form={editForm} isLoading={isLoading} />
              </Row>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Review Business Unit"
          centered
          open={showViewModal}
          footer={null}
          onCancel={() => {
            setShowViewModal(false); 
            editForm.resetFields();
          }}
        >
          <Form className='mt-3 mx-3' form={editForm} name="editForm" layout='vertical'>
            <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
              <Input disabled/>
            </Form.Item>
          
            <Form.Item>
              <Row className='d-flex justify-content-end'>
                  <Button danger onClick={()=>setShowViewModal(false)} disabled={isLoading}>Cancel</Button>
                  <Button type="text" onClick={()=>{setShowViewModal(false); setShowEditModal(true)}} icon={<EditOutlined/>}>Edit</Button>
                  <Button type="text" danger onClick={()=>{confirmDelete(clickedRowData)}} icon={<DeleteOutlined/>}>Delete</Button>
              </Row>
            </Form.Item>
          </Form>
        </Modal>
        {contextHolder}
    </Navbar>
  )
}